import React, { useState, useEffect, useRef } from 'react';
import ProductOptionsModal from './ProductOptionsModal';
import { useCart } from '../../context/CartContext';
import { useAppContext } from '../../context/AppContext';
import { flyToCart } from '../../utils/flyToCart';

const ProductCard = ({ product, onAddToCart }) => {
    const { settings } = useAppContext();
    const [showOptions, setShowOptions] = useState(false);
    const [notificationState, setNotificationState] = useState('hidden');
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const { addItem } = useCart();
    const addBtnRef = useRef(null);

    const handleAddToCart = () => {
        if (!product.custom_attributes || product.custom_attributes.length === 0) {
            addItem(product);
            setNotificationState('visible');
            flyToCart(addBtnRef.current); // #8 animación al carrito
        } else {
            setShowOptions(true);
        }
    };

    useEffect(() => {
        if (notificationState === 'visible') {
            const timer = setTimeout(() => {
                setNotificationState('exiting');
            }, 1500);
            return () => clearTimeout(timer);
        } else if (notificationState === 'exiting') {
            const exitTimer = setTimeout(() => {
                setNotificationState('hidden');
            }, 300);
            return () => clearTimeout(exitTimer);
        }
    }, [notificationState]);

    const getImageSrc = () => {
        if (product.image_base64) {
            return `data:image/jpeg;base64,${product.image_base64}`;
        }

        if (product.id) {
            return `http://localhost:9000/images/products/${product.id}`;
        }

        return null;
    };

    const imageSrc = getImageSrc();
    const hasImage = imageSrc != null;

    const handleImageLoad = () => {
        setImageLoaded(true);
        setImageError(false);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(false);
    };

    const hasCustomAttributes = product.has_custom_attributes || (product.custom_attributes && product.custom_attributes.length > 0);

    return (
        <>
            <style>
                {`
                    .notification-enter {
                        animation: slideInUp 300ms ease-out forwards;
                    }
                    .notification-exit {
                        animation: slideOutDown 300ms ease-in forwards;
                    }
                    
                    .product-card-hover {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    
                    .product-card-hover:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    }
                    
                    .add-button-hover {
                        transition: all 0.2s ease-in-out;
                    }
                    
                    .add-button-hover:hover {
                        transform: scale(1.1);
                    }
                    
                    .add-button-hover:active {
                        transform: scale(0.95);
                    }
                    
                    .image-skeleton {
                        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                        background-size: 200% 100%;
                        animation: loading 1.5s infinite;
                    }
                    
                    @keyframes loading {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                    
                    @keyframes slideInUp {
                        0% { 
                            opacity: 0; 
                            transform: translate(-50%, 20px) scale(0.8); 
                        }
                        100% { 
                            opacity: 1; 
                            transform: translate(-50%, 0) scale(1); 
                        }
                    }
                    
                    @keyframes slideOutDown {
                        0% { 
                            opacity: 1; 
                            transform: translate(-50%, 0) scale(1); 
                        }
                        100% { 
                            opacity: 0; 
                            transform: translate(-50%, 20px) scale(0.8); 
                        }
                    }
                    
                    @keyframes bounce-in {
                        0% { transform: scale(0) rotate(180deg); }
                        50% { transform: scale(1.1) rotate(180deg); }
                        100% { transform: scale(1) rotate(0deg); }
                    }
                    
                    .bounce-in {
                        animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
                    }
                `}
            </style>

            <div className="relative group">
                <div
                    ref={addBtnRef}
                    onClick={handleAddToCart}
                    className="product-card-hover rounded-2xl overflow-hidden relative flex flex-col"
                    style={{
                        background: '#ffffff',
                        border: '1px solid #ece7db',
                        boxShadow: '0 10px 26px rgba(0,0,0,0.10)',
                        height: '360px',
                        cursor: 'pointer'
                    }}
                >
                    <div className="relative overflow-hidden flex-shrink-0" style={{ height: '190px', background: '#ffffff', borderBottom: '1px solid #f1ece1' }}>
                        {!imageLoaded && !imageError && hasImage && (
                            <div className="absolute inset-0 image-skeleton"></div>
                        )}

                        {hasImage && !imageError ? (
                            <img
                                src={imageSrc}
                                alt={product.name}
                                className={`w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={handleImageLoad}
                                onError={handleImageError}
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <svg width="58" height="58" viewBox="0 0 24 24" fill="none" stroke="#c4b79a" strokeWidth="1.3">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 3v7a2 2 0 002 2v9M6 3v7M9 3v7M17 3c-1.5 2-2 4-2 7v11M15 10h4" />
                                </svg>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col flex-1" style={{ padding: '14px 16px' }}>
                        <h3 className="font-bold text-gray-900" style={{
                            fontSize: '1.05rem', lineHeight: 1.2,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                        }}>
                            {product.name}
                        </h3>

                        {product.description && product.description !== 'False' && (
                            <p className="text-gray-500" style={{
                                fontSize: '0.85rem', marginTop: 4, lineHeight: 1.3,
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                            }}>
                                {product.description}
                            </p>
                        )}

                        <div className="flex items-end justify-between mt-auto" style={{ paddingTop: 10 }}>
                            <div className="flex flex-col">
                                <span className="font-black" style={{ color: '#009542', fontSize: '1.35rem', lineHeight: 1 }}>
                                    {hasCustomAttributes
                                        ? `Desde $${product.price.toLocaleString('es-CL')}`
                                        : `$${product.price.toLocaleString('es-CL')}`}
                                </span>
                                {hasCustomAttributes && (
                                    <span className="text-xs text-gray-400 font-medium" style={{ marginTop: 2 }}>Precio base</span>
                                )}
                            </div>

                            <span style={{ color: '#6b7280', fontWeight: 900, fontSize: '1.6rem', lineHeight: 1 }}>›</span>
                        </div>
                    </div>
                </div>
            </div>

            {notificationState !== 'hidden' && (
                <div
                    className={`fixed bottom-24 left-1/2 z-50 rounded-xl shadow-lg ${notificationState === 'visible' ? 'notification-enter' : 'notification-exit'}`}
                    style={{
                        backgroundColor: settings?.button_color || '#009542',
                        color: 'white',
                        transform: 'translateX(-50%)',
                        padding: '14px 26px',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.28)'
                    }}
                >
                    <div className="flex items-center" style={{ gap: '10px', fontSize: '1.05rem', fontWeight: 700 }}>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>¡Agregado al carrito!</span>
                    </div>
                </div>
            )}

            {showOptions && (
                <ProductOptionsModal
                    product={product}
                    onClose={() => setShowOptions(false)}
                    onSubmit={(selectedQuantities, totalPrice, attributesDescription, attribute_lines, nested_attributes) => {
                        const productOriginalId = String(product.id).split('-custom-')[0];

                        addItem({
                            ...product,
                            id: `${productOriginalId}-custom-${Date.now()}`,
                            original_id: productOriginalId,
                            price: totalPrice,
                            base_price: product.price,
                            customDescription: attributesDescription,
                            // Cantidades completas elegidas (Odoo las compara contra su valor por defecto para cobrar los
                            // extras) y los extras de cada sub-combo: mismo formato que ProductList y el PDV.
                            selectedAttributes: selectedQuantities,
                            nested_attributes: nested_attributes || null,
                            attribute_lines: attribute_lines || [],
                            image_base64: product.image_base64,
                            hasOptions: true
                        });
                        setShowOptions(false);
                        setNotificationState('visible');
                    }}
                />
            )}
        </>
    );
};

export default ProductCard;